import React, { useState, useEffect } from 'react';
import { 
  BookOpen, 
  TrendingUp,
  Clock,
  Award,
  PlayCircle
} from 'lucide-react';
import { createClient } from '@supabase/supabase-js';

// Initialize Supabase client
const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userStats, setUserStats] = useState({
    coursesInProgress: 0,
    hoursLearned: 0,
    certifications: 0,
    completionRate: 0,
    recentProgress: {
      coursesStarted: 0,
      hoursThisWeek: 0,
      completionIncrease: 0
    }
  });
  const [userName, setUserName] = useState('');
  const [inProgressCourses, setInProgressCourses] = useState([]);

  useEffect(() => {
    const fetchDashboardData = async () => {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (!session) {
          setError('Please sign in to view your dashboard');
          setLoading(false);
          return;
        }
      try {
        setLoading(true);
        const { data: { user } } = await supabase.auth.getUser();
        
        // Fetch user profile
        const { data: profile } = await supabase
          .from('profiles')
          .select('first_name')
          .eq('id', user?.id)
          .single();

        setUserName(profile?.first_name || 'User');

        // Fetch enrollment statistics
        const { data: enrollments } = await supabase
          .from('enrollments')
          .select(`
            *,
            course:courses(
              id,
              title,
              thumbnail_url,
              duration
            )
          `)
          .eq('user_id', user.id);

        // Calculate statistics
        const now = new Date();
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        
        const stats = enrollments.reduce((acc, enrollment) => {
          const lastAccessed = new Date(enrollment.last_accessed);
          const progress = (enrollment.completed_lessons / enrollment.total_lessons) * 100;
          
          // Update total stats
          if (progress < 100) acc.coursesInProgress++;
          if (progress === 100) acc.certifications++;
          acc.hoursLearned += (enrollment.completed_lessons * 0.5); // Assuming 30 mins per lesson
          
          // Update weekly progress
          if (lastAccessed > weekAgo) {
            acc.recentProgress.hoursThisWeek += 0.5; // For each lesson accessed this week
          }
          
          return acc;
        }, {
          coursesInProgress: 0,
          hoursLearned: 0,
          certifications: 0,
          completionRate: 0,
          recentProgress: {
            coursesStarted: 0,
            hoursThisWeek: 0,
            completionIncrease: 5 // Example value, calculate based on historical data
          }
        });

        // Calculate completion rate
        stats.completionRate = enrollments.length > 0
          ? (stats.certifications / enrollments.length) * 100
          : 0;

        setUserStats(stats);

        // Fetch in-progress courses
        const inProgress = enrollments
          .filter(e => (e.completed_lessons / e.total_lessons) * 100 < 100)
          .map(e => ({
            id: e.course.id,
            title: e.course.title,
            progress: (e.completed_lessons / e.total_lessons) * 100,
            thumbnail: e.course.thumbnail_url || "/api/placeholder/400/225",
            completedLessons: e.completed_lessons,
            totalLessons: e.total_lessons,
            lastAccessed: formatLastAccessed(e.last_accessed)
          }))
          .sort((a, b) => new Date(b.lastAccessed) - new Date(a.lastAccessed))
          .slice(0, 3);

        setInProgressCourses(inProgress);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching dashboard data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const formatLastAccessed = (date) => {
    if (!date) return 'Never';
    const now = new Date();
    const lastAccess = new Date(date);
    const diffDays = Math.floor((now - lastAccess) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return `${Math.floor(diffDays / 7)} weeks ago`;
  };

  const statsCards = [
    {
      title: 'Courses in Progress',
      value: userStats.coursesInProgress,
      icon: BookOpen,
      trend: `+${userStats.recentProgress.coursesStarted} this week`
    },
    {
      title: 'Hours Learned',
      value: userStats.hoursLearned.toFixed(1),
      icon: Clock,
      trend: `+${userStats.recentProgress.hoursThisWeek.toFixed(1)} hrs this week`
    },
    {
      title: 'Certifications',
      value: userStats.certifications,
      icon: Award
    },
    {
      title: 'Course Completion',
      value: `${Math.round(userStats.completionRate)}%`,
      icon: TrendingUp,
      trend: `${userStats.recentProgress.completionIncrease}% increase`
    }
  ];

  const CourseProgressCard = ({ course }) => (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all overflow-hidden">
      <div className="flex items-center p-4">
        <img 
          src={course.thumbnail}
          alt={course.title}
          className="w-24 h-16 object-cover rounded-lg"
        />
        <div className="ml-4 flex-1">
          <h3 className="font-medium text-navy-900 mb-1">{course.title}</h3>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600">
              {course.completedLessons}/{course.totalLessons} Lessons
            </span>
            <span className="text-sm text-gray-600">{course.lastAccessed}</span>
          </div>
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-navy-600 rounded-full transition-all"
              style={{ width: `${course.progress}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-gray-600">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-red-600">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-navy-900">Welcome back, {userName}! 👋</h1>
          <p className="text-gray-600 mt-1">Continue your learning journey</p>
        </div>
        <button 
          onClick={() => window.location.href = '/courses'}
          className="flex items-center gap-2 bg-navy-600 text-white px-4 py-2 rounded-lg hover:bg-navy-700 transition-colors"
        >
          <BookOpen size={20} />
          <span>Start New Course</span>
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statsCards.map((card, index) => (
          <div key={index} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-gray-600 text-sm mb-1">{card.title}</p>
                <h3 className="text-2xl font-bold text-navy-900">{card.value}</h3>
                {card.trend && (
                  <p className="text-green-500 text-sm mt-2 flex items-center gap-1">
                    <TrendingUp size={16} />
                    {card.trend}
                  </p>
                )}
              </div>
              <div className="bg-navy-50 p-3 rounded-lg">
                <card.icon className="text-navy-600" size={24} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Continue Learning Section */}
      <div className="mt-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-navy-900">Continue Learning</h2>
          <button 
            onClick={() => window.location.href = '/my-courses'}
            className="text-navy-600 hover:text-navy-700"
          >
            View All
          </button>
        </div>
        <div className="grid grid-cols-1 gap-4">
          {inProgressCourses.map(course => (
            <CourseProgressCard key={course.id} course={course} />
          ))}
          {inProgressCourses.length === 0 && (
            <div className="text-center py-8 bg-white rounded-xl">
              <BookOpen size={48} className="mx-auto text-gray-400 mb-3" />
              <p className="text-gray-600">No courses in progress</p>
              <button 
                onClick={() => window.location.href = '/courses'}
                className="mt-4 text-navy-600 hover:text-navy-700"
              >
                Browse Courses
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;